import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { useTranslation } from 'react-i18next';
import { AnimatePresence } from 'framer-motion';
import { IGlobalStoreState } from '../../../store';
import { Page, PageContent } from '../../../components/UI';
import { EventDetailsHeader } from '../../../components/event';
import { AlertType, KeyErrors } from '../../../store/enums';
import { getTicketById, issueTicket } from '../../../store/user/actions';
import { IEmailForm } from '../../../components/forms';
import { IErrorStoreState } from '../../../store/app/types';
import { EventHelper } from '../../../helpers';
import { IProfileTicketStoreState } from '../../../store/ticket/ProfileTicketStoreState';
import { useRoutesHelper } from '../../../helpers/routesHelper';
import { setResultInfo } from '../../../store/resultInformarion/actions';
import {
  RenderForm,
  RenderIssuedInfo,
  RenderTicketSector,
} from './userTicketDetailsComponents/userTicketDetailsComponents';

export type UserTicketDetailsParamsType = {
  masterTicketId: string;
  ticketId: string;
};

const UserTicketDetailsPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { getProfileRoute } = useRoutesHelper();
  const history = useHistory();

  const isFinalized = useSelector<IGlobalStoreState, boolean>((state) => state.app.isFinalized);
  const error = useSelector<IGlobalStoreState, IErrorStoreState>((state) => state.app.error);
  const isAuthorized = useSelector<IGlobalStoreState, boolean>((state) => state.app.isAuthorized);

  const [ticket, setTicket] = useState<IProfileTicketStoreState>(null);
  const [errors, setErrors] = useState(null);

  const { masterTicketId, ticketId } = useParams<UserTicketDetailsParamsType>();

  const initTicket = useCallback(
    (masterTicketId: string, ticketId?: string) => {
      if (!masterTicketId) {
        return;
      }
      (async () => dispatch(getTicketById(masterTicketId, ticketId)))().then((ticket: any) => {
        if (!ticket || !ticket.masterTicketId) {
          dispatch(
            setResultInfo({
              type: AlertType.Error,
              key: KeyErrors.TicketNotFound,
              title: 'Alert.WaitForTheServerToCompleteThePayment',
              buttons: [
                {
                  title: 'Result.GoToMainPage',
                  callback: () => history.push('/'),
                },
              ],
            })
          );
          return;
        }

        setTicket(ticket);
      });
    },
    [dispatch, history]
  );

  useEffect(() => {
    if (isFinalized) {
      initTicket(masterTicketId, ticketId);
    }
  }, [initTicket, isFinalized, masterTicketId, ticketId]);

  useEffect(() => {
    if (isFinalized || isAuthorized) {
      initTicket(masterTicketId, ticketId);
    }
  }, [isFinalized, isAuthorized, masterTicketId, ticketId]);

  useEffect(() => {
    if (!isAuthorized) {
      history.push(getProfileRoute());
    }
  }, [getProfileRoute, history, isAuthorized]);

  useEffect(() => {
    setErrors({ form: error });
  }, [error?.details, error?.message]);

  const handleEmailFormSubmit = useCallback(
    (formModel: IEmailForm) => {
      if (formModel) {
        (async () => dispatch(issueTicket(ticketId, formModel.email)))().then((ticket: any) => {
          setTicket(ticket);
        });
      }
    },
    [dispatch, ticketId]
  );

  if (!isFinalized || !ticket || !ticket.masterTicketId) {
    return null;
  }
  const { event, ticketQuota, seatTitle, rowTitle, issuedTo } = ticket;
  const isIssued = !!ticket.issuedTo;
  return (
    <Page title={`${t('TicketDetails.TicketIssue')} | ${EventHelper.getEventDetailsMetaDescription(event)}`}>
      <EventDetailsHeader event={ticket.event} noTitle stretch />
      <Content roundCorners>
        <Title>{event.title}</Title>
        <Subtitle>{event.subtitle}</Subtitle>
        {<RenderTicketSector ticketQuota={ticketQuota} seatTitle={seatTitle} rowTitle={rowTitle} />}
        <AnimatePresence>
          {!isIssued && <RenderForm errors={errors} handleEmailFormSubmit={handleEmailFormSubmit} />}
          {isIssued && <RenderIssuedInfo issuedTo={issuedTo} />}
        </AnimatePresence>
      </Content>
    </Page>
  );
};

const Content = styled(PageContent as any)`
  padding-top: 0;
  flex: 0;
  min-height: 250px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-family: ${(props) => props.theme.fonts.bolderFont};
  text-align: center;
  margin: 0 0 5px 0;
  position: relative;
  top: -20px;
`;

export const Subtitle = styled.h2`
  font-size: 14px;
  margin: 0 0 15px 0;
  text-align: center;
  position: relative;
  top: -20px;
`;

export default UserTicketDetailsPage;
